/** @jsx jsx */
/** @jsxFrag null */
import {jsx} from 'panel';

import {SvgIconEl} from 'common/components/svg-icon';
import {SelectEl} from 'common/components/select';
import {TooltipEl} from 'common/components/tooltip';
import {CalendarInputEl} from 'common/widgets/date-range-picker/calendar-input';
import {CheckboxEl} from 'common/components/checkbox';
import {DisplayType} from 'common/components/tooltip/types';
import type {DateRangePicker} from 'common/widgets/date-range-picker/index';

export function template(this: DateRangePicker) {
  const {dateRange} = this.state;
  const invalidDateRangeError = !this.helpers.isValidDateRange();
  const dateLimitedError = !invalidDateRangeError && !this.helpers.isWithinMaxHistoryLimit();

  const {window} = dateRange;

  return (
    <div sel='.date-range-picker-container'>
      {this.helpers.showUpsell() && (
        <div sel='.upsell-header'>
          <SvgIconEl attrs={{icon: `lightning-bolt`}} />
          <div sel='.upsell-text'>
            <a sel='.upgrade-link' attrs={{href: `/pricing`, target: `_blank`}}>
              Upgrade your plan to query beyond {this.helpers.maxHistoryDaysText()}
            </a>
          </div>
        </div>
      )}
      <div sel='.builder-pane-dropdown-container'>
        <SelectEl
          params={{
            sections: dateRange.getDateRangeTypeDisplaySections(dateRange.type),
          }}
          on={{
            change: this.helpers.changedDateRangeType,
          }}
        />
      </div>
      <div sel='.date-range-picker'>
        {[
          this.helpers.DateRangeType.RelativeAfter,
          this.helpers.DateRangeType.RelativeBefore,
          this.helpers.DateRangeType.NotRelativeAfter,
        ].includes(dateRange.type) ? (
          <div sel='.relative-range-picker'>
            <input
              sel='.relative-range-value'
              class={{
                error: invalidDateRangeError || dateLimitedError,
              }}
              attrs={{
                type: `number`,
                value: !Number.isInteger(window!.value) ? `` : window!.value,
              }}
              on={{
                mouseenter: this.helpers.mouseenterRangeInput,
                mouseleave: this.helpers.mouseleaveRangeInput,
                blur: this.helpers.blurredRangeInput,
                focus: this.helpers.focusedRangeInput,
                change: (ev) => ev.stopPropagation(),
                input: this.helpers.changedRelativeRangeValue,
              }}
              hook={{insert: (vnode: any) => requestAnimationFrame(() => vnode.elm.select())}}
              key={dateRange.type}
            />
            <SelectEl
              attrs={{
                options: dateRange.getTimeUnitOptions().map((unit) => this.helpers.TIME_UNIT_DISPLAY_NAMES[unit]),
                'selected-option': this.helpers.TIME_UNIT_DISPLAY_NAMES[window!.unit],
              }}
              on={{
                change: this.helpers.changedRelativeRangeUnit,
              }}
            />
            {dateLimitedError && this.helpers.isRangeInputActive() && (
              <TooltipEl
                attrs={{
                  type: DisplayType.Error,
                  'always-show': true,
                }}
              >
                {this.helpers.dateLimitedTooltipText()}
              </TooltipEl>
            )}
          </div>
        ) : (
          <>
            <CalendarInputEl
              attrs={this.helpers.filterAttributes({
                from: dateRange.getCalendarFromDate() || ``,
                to: dateRange.getCalendarToDate() || ``,
                'max-date': this.attr(`max-date`) || dateRange.getCalendarMaxDate(),
                'max-history-days': this.helpers.maxHistoryDays(),
                'is-range':
                  dateRange.type === this.helpers.DateRangeType.Between ||
                  dateRange.type === this.helpers.DateRangeType.NotBetween,
                'min-days-apart': 1,
                'show-upsell': this.helpers.showUpsell(),
              })}
              on={{
                change: this.helpers.changedDates,
                draw: this.helpers.propagateEvent,
              }}
              key={dateRange.type}
              hook={{
                insert: this.helpers.handleInsertCalendarInput,
              }}
            />
            {dateRange.canUseRolling() && (
              <CheckboxEl
                sel='.rolling-checkbox'
                attrs={{checked: dateRange.isRolling()}}
                on={{click: this.helpers.clickedRollingCheckbox}}
              >
                <div sel='.rolling-text'>
                  {this.helpers.getRollingCheckboxText()}
                  <div sel='.rolling-info-icon'>
                    <SvgIconEl attrs={{icon: `info`}} />
                    <TooltipEl>{this.helpers.getRollingInfoText()}</TooltipEl>
                  </div>
                </div>
              </CheckboxEl>
            )}
          </>
        )}
        {this.helpers.showMaxDateContext() && (
          <div sel='.date-range-picker-context'>
            <div sel='.date-range-picker-context-text'>
              {this.helpers.maxDateContextText()}
              <div
                sel='.date-range-picker-context-icon'
                class={{'override-z-index': this.helpers.overrideMaxDateContextZIndex()}}
              >
                <SvgIconEl attrs={{icon: `info`}} />
                <TooltipEl>{this.helpers.maxDateTooltipText()}</TooltipEl>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
